"use client";

import { CopyIcon, ExternalLinkIcon } from "lucide-react";

import {
  ContextMenuContent,
  ContextMenuCheckboxItem,
  ContextMenuItem,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/src/components/shadcn/ui/context-menu";
import {
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/src/components/shadcn/ui/dropdown-menu";
import { useToast } from "@/src/hooks/use-toast";

export enum MenuActionType {
  Item = "item",
  Radio = "radio",
  Checkbox = "checkbox",
  CustomSubmenu = "custom_submenu",
}

export type ItemAction = {
  type: MenuActionType.Item;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
};

export type SubmenuRadioOption = {
  value: string;
  label: string;
  content?: React.ReactNode;
};

export type SubmenuRadioAction = {
  type: MenuActionType.Radio;
  label: string;
  icon?: React.ReactNode;
  value: string | undefined;
  options: SubmenuRadioOption[];
  onValueChange?: (value: string) => void;
  subContentClassName?: string;
};

export type SubmenuCheckboxOption = {
  value: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  content?: React.ReactNode;
};

export type SubmenuCheckboxAction = {
  type: MenuActionType.Checkbox;
  label: string;
  icon?: React.ReactNode;
  options: SubmenuCheckboxOption[];
  onCheckedChange?: (value: string, checked: boolean) => void;
  subContentClassName?: string;
};

export type SubmenuCustomAction = {
  type: MenuActionType.CustomSubmenu;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  subContentClassName?: string;
};

export type MenuAction = ItemAction | SubmenuRadioAction | SubmenuCheckboxAction | SubmenuCustomAction;

export type ActionGroup = MenuAction[];

export type EntityMenuEntity = { href?: string; id: string; label: string };

interface EntityMenuProps {
  isContextMenu?: boolean;
  entity: EntityMenuEntity;
  actionGroups: ActionGroup[];
  align?: "start" | "center" | "end";
  className?: string;
  onCloseAutoFocus?: (e: Event) => void;
  showCopyId?: boolean;
}

export function EntityMenu({
  isContextMenu = false,
  entity,
  actionGroups,
  align = "end",
  className,
  onCloseAutoFocus,
  showCopyId = true,
}: EntityMenuProps) {
  const { toast } = useToast();
  const MenuContent = isContextMenu ? ContextMenuContent : DropdownMenuContent;
  const Item = isContextMenu ? ContextMenuItem : DropdownMenuItem;
  const CheckboxItem = isContextMenu ? ContextMenuCheckboxItem : DropdownMenuCheckboxItem;
  const Separator = isContextMenu ? ContextMenuSeparator : DropdownMenuSeparator;
  const Shortcut = isContextMenu ? ContextMenuShortcut : DropdownMenuShortcut;
  const Sub = isContextMenu ? ContextMenuSub : DropdownMenuSub;
  const SubContent = isContextMenu ? ContextMenuSubContent : DropdownMenuSubContent;
  const SubTrigger = isContextMenu ? ContextMenuSubTrigger : DropdownMenuSubTrigger;
  const RadioGroup = isContextMenu ? ContextMenuRadioGroup : DropdownMenuRadioGroup;
  const RadioItem = isContextMenu ? ContextMenuRadioItem : DropdownMenuRadioItem;

  const renderLabel = (label: string, icon?: React.ReactNode) => {
    return (
      <span className="flex w-full items-center">
        {icon ? <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">{icon}</span> : null}
        <span className="truncate">{label}</span>
      </span>
    );
  };

  const handleCopyId = (id: string, label: string) => {
    const doCopy = async () => {
      try {
        await navigator.clipboard.writeText(id);
        toast({ title: `Copied ${label} to clipboard` });
      } catch {
        toast({
          title: "Error",
          description: `Failed to copy ${label}`,
          variant: "destructive",
        });
      }
    };

    doCopy();
  };

  const expandAction = (action: MenuAction, groupIndex: number, actionIndex: number): React.ReactNode[] => {
    if (action.type === MenuActionType.Item) {
      return [
        <Item key={`${groupIndex}-${actionIndex}`} onClick={action.onClick} disabled={action.disabled}>
          {renderLabel(action.label, action.icon)}
          {action.shortcut ? <Shortcut className="shrink-0 whitespace-nowrap">{action.shortcut}</Shortcut> : null}
        </Item>,
      ];
    }

    if (action.type === MenuActionType.Radio) {
      return [
        <Sub key={`${groupIndex}-${actionIndex}`}>
          <SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger>
          <SubContent className={action.subContentClassName}>
            <RadioGroup value={action.value} onValueChange={action.onValueChange}>
              {action.options.map((opt) => (
                <RadioItem key={opt.value} value={opt.value}>
                  {opt.content ?? opt.label}
                </RadioItem>
              ))}
            </RadioGroup>
          </SubContent>
        </Sub>,
      ];
    }

    if (action.type === MenuActionType.Checkbox) {
      return [
        <Sub key={`${groupIndex}-${actionIndex}`}>
          <SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger>
          <SubContent className={action.subContentClassName}>
            {action.options.map((opt) => (
              <CheckboxItem
                key={opt.value}
                checked={opt.checked}
                disabled={opt.disabled}
                onCheckedChange={(checked) => action.onCheckedChange?.(opt.value, checked === true)}
              >
                {opt.content ?? opt.label}
              </CheckboxItem>
            ))}
          </SubContent>
        </Sub>,
      ];
    }

    if (action.type === MenuActionType.CustomSubmenu) {
      return [
        <Sub key={`${groupIndex}-${actionIndex}`}>
          <SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger>
          <SubContent className={action.subContentClassName}>{action.content}</SubContent>
        </Sub>,
      ];
    }

    return [];
  };

  return (
    <MenuContent
      align={align}
      className={className}
      onClick={(e) => e.stopPropagation()}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {entity.href != null && (
        <Item onClick={() => window.open(entity.href!, "_blank", "noopener,noreferrer")}>
          {renderLabel("Open in New Tab", <ExternalLinkIcon className="h-4 w-4 text-muted-foreground" />)}
        </Item>
      )}
      {showCopyId && (
        <Item onClick={() => handleCopyId(entity.id, entity.label)}>
          {renderLabel(`Copy ${entity.label}`, <CopyIcon className="h-4 w-4 text-muted-foreground" />)}
        </Item>
      )}
      {actionGroups.map((group, groupIndex) => (
        <span key={groupIndex}>
          {(entity.href != null || showCopyId) && <Separator />}
          {group.flatMap((action, actionIndex) => expandAction(action, groupIndex, actionIndex))}
        </span>
      ))}
    </MenuContent>
  );
}
